import { patternErrors, schemaErrors} from './simplifiedSchemaErrors';
import { isSigningAllowedForModule } from './utilityFunctions';
import { FNV23_ARCHITECTURES, UI_SUPPORTED_ARCHITECTURES } from './constants';

export function schemaValidation(schema, json, architectureType = "", signingAllowedModules = []) {
    if (!schema) {return;}
    schema = structuredClone(schema);
    const Ajv = require("ajv");
    const errorCollection = true;
    const ajvOptions = {
        strict: false
    };
    
    ajvOptions[['all', 'Errors'].join('')] = errorCollection;
    
    const ajv = new Ajv(ajvOptions);
    const addFormats = require("ajv-formats")
    addFormats(ajv, ["uri"])
    
    if (FNV23_ARCHITECTURES.includes(architectureType)) {
        schema.properties.machineList.fnv2and3VersionValidation= true;
        ajv.addKeyword({
            keyword: "fnv2and3VersionValidation",
            type: "array",
            compile: (schema, parentSchema) => 
            function validate (data) {
                let valid = true
                const errorList = []
                // Check if major version is > 0, no versions can be less than 1.0.0
                for (let i = 0; i < data.length; i++) {
                    const machine = data[i];
                    for (let j = 0; j < machine.variantList.length; j++) {
                        const variant = machine.variantList[j];
                        for (let k = 0; k < variant.clusterList.length; k++) {
                            const cluster = variant.clusterList[k];

                            let majorVersion = cluster.clusterVersion?.split('.')[0] ?? "0";
                            majorVersion = parseInt(majorVersion);
                            
                            if (majorVersion < 1) {
                                errorList.push({
                                    keyword: "fnv2and3VersionValidation",
                                    instancePath: "/machineList/" + i + "/variantList/" + j + "/clusterList/" + k + "/clusterVersion",
                                    schemaPath: "#/fnv2and3VersionValidation",
                                    params: {keyword: 'invalid_clusterVersion'},
                                    message: `Cluster version cannot be below 1.0.0. Found: ${cluster.clusterVersion}`
                                });
                                valid = false
                            }
                        }
                    }
                }
                validate.errors = errorList;
                return valid;
            },
            errors: true
        });
    }

    if (UI_SUPPORTED_ARCHITECTURES.includes(architectureType)) {
        schema.signRequestPerModuleValidation = true;
        ajv.addKeyword({
            keyword: "signRequestPerModuleValidation",
            compile: (schema, parentSchema) => 
            function validate (data) {
                let valid = true;
                const errorList = [];
                const moduleName = data.moduleInformation?.moduleName ?? "";
                const machineList = data.machineList ?? [];
                const isAllowToSign = isSigningAllowedForModule(signingAllowedModules, moduleName);

                if (!isAllowToSign) {
                    for (let i = 0; i < machineList.length; i++) {
                        const machine = machineList[i];
                        for (let j = 0; j < machine.variantList.length; j++) {
                            const variant = machine.variantList[j];
                            for (let k = 0; k < variant.clusterList.length; k++) {
                                const cluster = variant.clusterList[k];
                                if (cluster.swSignRequest === "DEV_SIGN" || cluster.swSignRequest === "PROD_SIGN") {
                                    errorList.push({
                                        keyword: "signRequestPerModuleValidation",
                                        instancePath: "/machineList/" + i + "/variantList/" + j + "/clusterList/" + k + "/swSignRequest",
                                        schemaPath: "#/signRequestPerModuleValidation",
                                        params: {keyword: 'signing_not_allowed_for_specified_module'},
                                        message: `Signing is currently not allowed for module ${moduleName}. Please select "NO_SIGN".`
                                    });
                                    valid = false
                                }
                            } 
                        }
                
                    }
                }
                validate.errors = errorList;
                return valid;
            },
            errors: true
        });
    }


    const validator = ajv.compile(schema);
    const srpValid = validator(json);
    let errors = validator?.errors;

    // errors will contain an array of individual errors if any are present
    return {isValid: srpValid, errors:errors};
}

const boxStyleAJV = {
    overflowY: "scroll",
    width: "99%",
    borderRadius: "5px",
    color: "black",
    backgroundColor: "#ffd4e9",
    whiteSpace: "pre",
    position: "relative",
    padding: "10px",
    margin: "10px 5px",
    fontSize: "12pt"
}

function fetchErrorMessage(error){

    // Unifies the field so no matter the indices inside, it will always reach the same key
    console.log("Error: ", error);
    if (error.keyword === "pattern"){
        return patternErrors[error.params.pattern] ?? error.message;
    }
    
    if(error.keyword === "format"){
        return schemaErrors[error.params.format] ?? "Unknown Error:\n =>" + error.message + "\n =>" + JSON.stringify(error.params);
    }

    if(error.keyword === "uniqueItems"){
        return schemaErrors[error.keyword] + `(items ${error.params.i + 1} and ${error.params.j + 1} are the same)`
    }

    if(error.keyword === "minimum"){
        return schemaErrors[error.keyword] + `${error.params.limit}`
    }

    // Temporary fix for boolean type errors
    if (error.keyword === "type" && error.params.type === "boolean") {
        return schemaErrors["enum"];
    }

    if (error.keyword === "swSignRequestValidation") {
        return "- " + error.message;
    }

    if (error.keyword === "fnv2and3VersionValidation") {
        return "- " + error.message;
    }

    if (error.keyword === "signRequestPerModuleValidation") {
        return "- " + error.message;
    }

    

    // Fetches precanned version based on the keyword, or a full readout if it isn't accounted for
    return schemaErrors[error.keyword] ?? " - Unknown Error:\n =>" + error.message + "\n =>" + JSON.stringify(error.params);
}

export function buildSchemaErrorReadoutUI(errors){
    let consolidatedErrors = {};

    errors.forEach((error)=>{

        //skip conditional errors
        if(error.keyword === "if"){ return; }

        if(consolidatedErrors.hasOwnProperty(error.instancePath)){

            // If the keyword is "oneOf" make sure the error appears at the top of the list for readability.
            if(error.keyword === "oneOf"){
                let messages = consolidatedErrors[error.instancePath]["errorMessages"];
                consolidatedErrors[error.instancePath]["errorMessages"] = [fetchErrorMessage(error), ...messages]
                return;
            }

            consolidatedErrors[error.instancePath]["errorMessages"].push(fetchErrorMessage(error))
            return;
        }

        //fetch user readable field and error message
        consolidatedErrors[error.instancePath]={
            "field":error.instancePath,
            "errorMessages":[fetchErrorMessage(error)]
        }
    })

    let errorsReadout = Object.keys(consolidatedErrors).map((key)=>(
 
            <div id={`schemaError_${key}`} key={`schemaError_${key}`} style={boxStyleAJV}>
                {consolidatedErrors[key].field + ":\n"}
                {consolidatedErrors[key].errorMessages.join("\n ")}
            </div>
    ))

    return errorsReadout
}